import React, { useEffect, useState, useMemo, useCallback } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import { navigateTo } from '@/Utils/routing';
import { useUsers } from '@/Recoil/User';
import { useLikes, useBookmarks } from '@/Recoil/Social';
import { FastAvatar } from '@/Components/FastAvatar';
import PostCard from '@/Components/social/PostCard';
import LoadingView from '@/Components/social/LoadingView';
import IsometricRoom, { RoomEditor } from '@/Components/IsometricRoom';
import { searchPosts } from '@/Data/Social/Post';
import { contentFilter } from '@/Data/Social/ContentFilter';
import ss from '@/Data/Session';
import type { Post } from '@/Data/Social';
import moment from 'moment-timezone';
import './userProfile.scss';

const UserProfile = () => {
    // Get addressid from URL path
    const pathParts = window.location.pathname.split('/');
    const addressid = pathParts[pathParts.length - 1];

    const [loading, setLoading] = useState(true);
    const [userPosts, setUserPosts] = useState<Post[]>([]);
    const [editingRoom, setEditingRoom] = useState(false);

    // Check if viewing own profile
    const currentUserAddressId = ss.getCurrentSession().addressid;
    const isOwnProfile = addressid === currentUserAddressId;

    // State hooks
    const { profiles, syncProfiles } = useUsers();
    const { syncUserLikes } = useLikes();
    const { syncUserBookmarks } = useBookmarks();

    const userProfile = profiles[addressid || ''];

    // Load user posts
    const loadUserPosts = useCallback(async () => {
        if (!addressid) { return; }

        const result = await searchPosts({ addressid, limit: 200, offset: 0 });
        if (result) { setUserPosts(result); }
        return result;
    }, [addressid]);

    // Initialize data on mount
    useEffect(() => {
        if (!addressid) { return; }

        const init = async () => {
            setLoading(true);
            await Promise.all([
                syncProfiles([addressid]),
                loadUserPosts(),
                syncUserLikes(),
                syncUserBookmarks(),
            ]);
            setLoading(false);
        };

        init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addressid]);

    // Create posts sections grouped by month
    const postSections = useMemo(() => {
        const validPosts = userPosts.filter((post): post is Post =>
            !!post?.created_at &&
            !contentFilter.isPostFiltered(post.postid) &&
            !contentFilter.isUserFiltered(post.addressid) &&
            !contentFilter.hasFilteredHashtags(post.hashtags)
        );

        // Sort by newest first
        const sortedPosts = validPosts.sort((a, b) => (b.created_at ?? 0) - (a.created_at ?? 0));

        // Group by month
        const grouped = sortedPosts.reduce((acc, post) => {
            const month = moment((post.created_at ?? 0) * 1000).tz('asia/seoul').format('YYYY-MM');
            if (!acc[month]) { acc[month] = []; }
            acc[month]!.push(post);
            return acc;
        }, {} as Record<string, Post[]>);

        return Object.entries(grouped).map(([month, posts]) => ({
            title: month,
            data: posts
        }));
    }, [userPosts]);

    const displayName  = userProfile?.name || 'User';
    const displayImage = userProfile?.image;
    const postCount    = userPosts.length;

    if (loading) {
        return <LoadingView />;
    }

    return (
        <div className="UserProfilePage">
            <div className="Content">
                {/* Profile Box */}
                <section className="ProfileBox">
                    <div className="ProfileHeader">
                        <div className="ProfileImageContainer">
                            <FastAvatar
                                uri={displayImage}
                                userid={addressid || ''}
                                size={102}
                                style={{ borderRadius: '50%' }}
                            />
                        </div>

                        <div className="ProfileInfo">
                            <div className="UsernameSection">
                                <div>
                                    <span className="DisplayName">{displayName}</span>
                                    <span className="Username">@{addressid}</span>
                                </div>
                            </div>

                            <div className="StatsSection">
                                <div className="StatItem">
                                    <span className="StatLabel">Post</span>
                                    <span className="StatNumber">{postCount}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* User's Room */}
                    <div className="RoomContainer">
                        <div className="RoomHeader">
                            <h3 className="RoomTitle">{displayName}'s Room</h3>
                            {isOwnProfile && !editingRoom && (
                                <button className="EditRoomButton" onClick={() => setEditingRoom(true)}>
                                    <EditIcon /> Edit
                                </button>
                            )}
                        </div>
                        {editingRoom ? (
                            <RoomEditor addressid={addressid || ''} onClose={() => setEditingRoom(false)} />
                        ) : (
                            <IsometricRoom addressid={addressid || ''} />
                        )}
                    </div>
                </section>

                {/* Posts Section */}
                <section className="PostsSection">
                    {postSections.length === 0 ? (
                        <div className="EmptyState">
                            <h3 className="EmptyText">No posts yet</h3>
                            <p className="EmptySubtext">This user hasn't posted yet</p>
                        </div>
                    ) : (
                        postSections.map(section => (
                            <div key={section.title} className="MonthSection">
                                <div className="MonthDivider">
                                    <span className="MonthText">{section.title}</span>
                                </div>
                                <div className="PostsList">
                                    {section.data.map((post, index) => (
                                        <div key={post.postid} className="PostItem">
                                            {post.parent_postid && (
                                                <span
                                                    className="ReplyToText"
                                                    onClick={() => navigateTo(`/social/post/${post.parent_postid}`)}
                                                >
                                                    Reply to post <span className="ReplyPostId">{post.parent_postid}</span>
                                                </span>
                                            )}
                                            <PostCard post={post} />
                                            {index < section.data.length - 1 && <div className="Separator" />}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))
                    )}
                </section>
            </div>
        </div>
    );
};

export default UserProfile;
